import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useStore } from '../data/store';
import { supabase } from '../lib/supabase';
import {
  Shield,
  Users,
  Calendar,
  Building2,
  Plus,
  CreditCard as Edit,
  Trash2,
  X,
  Save,
  LogOut,
  Eye,
  EyeOff,
  BarChart3,
  Image as ImageIcon,
  Mail,
  Trophy,
} from 'lucide-react';

interface GameFormData {
  date: string;
  time: string;
  time2: string;
  opponent: string;
  location: 'Home' | 'Away';
  venue: string;
  address: string;
  isDoubleHeader: boolean;
}

interface PlayerFormData {
  name: string;
  number: string;
  positions: string[];
  image: string;
  bio: string;
  hometown: string;
  role: 'player' | 'gameday';
  title?: string;
}

interface SponsorFormData {
  name: string;
  logo: string;
  website: string;
  description: string;
  tier: 'gold' | 'silver';
  active: boolean;
}

interface PhotoFormData {
  image_url: string;
  caption: string;
  year: number;
  display_order: number;
}

const Admin = () => {
  const navigate = useNavigate();
  const {
    players,
    games,
    sponsors,
    addGame,
    addPlayer,
    addSponsor,
    updateGame,
    updatePlayer,
    updateSponsor,
    deleteGame,
    deletePlayer,
    deleteSponsor,
  } = useStore();

  const [authenticated, setAuthenticated] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<'games' | 'players' | 'sponsors' | 'statistics' | 'photos' | 'contact'>('games');
  const [showGameModal, setShowGameModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingGame, setEditingGame] = useState<any>(null);
  const [editingPlayer, setEditingPlayer] = useState<any>(null);
  const [editingSponsor, setEditingSponsor] = useState<any>(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [scoringGame, setScoringGame] = useState<any>(null);
  const [scoreForm, setScoreForm] = useState<{
    cardinals: string;
    opponent: string;
    game1_cardinals: string;
    game1_opponent: string;
    game2_cardinals: string;
    game2_opponent: string;
  }>({
    cardinals: '',
    opponent: '',
    game1_cardinals: '',
    game1_opponent: '',
    game2_cardinals: '',
    game2_opponent: '',
  });
  const [selectedPlayerForStats, setSelectedPlayerForStats] = useState<any>(null);
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [editingStats, setEditingStats] = useState<any>(null);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([String(new Date().getFullYear())]);
  const [showNewSeasonInput, setShowNewSeasonInput] = useState(false);
  const [newSeasonInput, setNewSeasonInput] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isSavingStats, setIsSavingStats] = useState(false);
  const [selectedStatsSeason, setSelectedStatsSeason] = useState<string>(String(new Date().getFullYear()));
  const [campPhotos, setCampPhotos] = useState<any[]>([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<any>(null);
  const [photoForm, setPhotoForm] = useState<PhotoFormData>({
    image_url: '',
    caption: '',
    year: new Date().getFullYear(),
    display_order: 0,
  });
  const [contactSubmissions, setContactSubmissions] = useState<any[]>([]);

  const [gameForm, setGameForm] = useState<GameFormData>({
    date: '',
    time: '',
    time2: '',
    opponent: '',
    location: 'Home',
    venue: '',
    address: '',
    isDoubleHeader: false,
  });

  const [playerForm, setPlayerForm] = useState<PlayerFormData>({
    name: '',
    number: '',
    positions: [],
    image: '',
    bio: '',
    hometown: '',
    role: 'player',
    title: '',
  });

  const [sponsorForm, setSponsorForm] = useState<SponsorFormData>({
    name: '',
    logo: '',
    website: '',
    description: '',
    tier: 'silver',
    active: true,
  });

  const [statsForm, setStatsForm] = useState({
    season: String(new Date().getFullYear()),
    games_played: 0,
    at_bats: 0,
    hits: 0,
    runs: 0,
    rbis: 0,
    doubles: 0,
    triples: 0,
    home_runs: 0,
    walks: 0,
    strikeouts: 0,
    stolen_bases: 0,
    innings_pitched: 0,
    earned_runs: 0,
    pitching_strikeouts: 0,
    walks_allowed: 0,
    putouts: 0,
    assists: 0,
    errors: 0,
  });

  const fetchAllData = async () => {
    setLoadingData(true);
    try {
      const [playersRes, gamesRes, sponsorsRes] = await Promise.all([
        supabase.from('players').select('*').order('name'),
        supabase.from('games').select('*').order('date'),
        supabase.from('sponsors').select('*').order('name'),
      ]);

      if (playersRes.error) {
        console.error('Players fetch error:', playersRes.error);
      }
      if (gamesRes.error) {
        console.error('Games fetch error:', gamesRes.error);
      }
      if (sponsorsRes.error) {
        console.error('Sponsors fetch error:', sponsorsRes.error);
      }

      return {
        players: playersRes.data || [],
        games: gamesRes.data || [],
        sponsors: sponsorsRes.data || [],
      };
    } catch (error) {
      console.error('Error fetching data:', error);
      return {
        players: [],
        games: [],
        sponsors: [],
      };
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          const { data: adminCheck } = await supabase
            .rpc('is_admin')
            .single();

          if (adminCheck) {
            setAuthenticated(true);
            setIsAdmin(true);
            await fetchAllData();
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setInitializing(false);
      }
    };

    checkExistingAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setAuthenticated(false);
        setIsAdmin(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setAuthError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginData.email.trim(),
        password: loginData.password,
      });

      if (error) {
        console.error('Login error:', error);
        setAuthError('Invalid credentials');
        setLoggingIn(false);
        return;
      }

      if (data.session) {
        const { data: adminCheck, error: adminError } = await supabase
          .rpc('is_admin');

        if (adminError) {
          console.error('Admin check error:', adminError);
          await supabase.auth.signOut();
          setAuthError('You do not have admin access');
          setLoggingIn(false);
          return;
        }

        if (!adminCheck) {
          await supabase.auth.signOut();
          setAuthError('You do not have admin access');
          setLoggingIn(false);
          return;
        }

        setAuthenticated(true);
        setIsAdmin(true);
        await fetchAllData();
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('An error occurred. Please try again.');
    }

    setLoggingIn(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthenticated(false);
    setIsAdmin(false);
    setLoginData({ email: '', password: '' });
    setAuthError(null);
    navigate('/');
  };

  const resetGameForm = () => {
    setGameForm({
      date: '',
      time: '',
      time2: '',
      opponent: '',
      location: 'Home',
      venue: '',
      address: '',
      isDoubleHeader: false,
    });
    setEditingGame(null);
  };

  const resetPlayerForm = () => {
    setPlayerForm({
      name: '',
      number: '',
      positions: [],
      image: '',
      bio: '',
      hometown: '',
      role: 'player',
      title: '',
    });
    setEditingPlayer(null);
  };

  const resetSponsorForm = () => {
    setSponsorForm({
      name: '',
      logo: '',
      website: '',
      description: '',
      tier: 'silver',
      active: true,
    });
    setEditingSponsor(null);
  };

  const resetStatsForm = () => {
    setStatsForm({
      season: String(new Date().getFullYear()),
      games_played: 0,
      at_bats: 0,
      hits: 0,
      runs: 0,
      rbis: 0,
      doubles: 0,
      triples: 0,
      home_runs: 0,
      walks: 0,
      strikeouts: 0,
      stolen_bases: 0,
      innings_pitched: 0,
      earned_runs: 0,
      pitching_strikeouts: 0,
      walks_allowed: 0,
      putouts: 0,
      assists: 0,
      errors: 0,
    });
    setEditingStats(null);
    setShowNewSeasonInput(false);
    setNewSeasonInput('');
  };

  const fetchPlayerStats = async () => {
    try {
      const { data, error } = await supabase
        .from('player_statistics')
        .select('*')
        .order('season', { ascending: false });

      if (error) throw error;
      setPlayerStats(data || []);

      const currentYear = String(new Date().getFullYear());
      const seasons = [...new Set((data || []).map((s: any) => s.season as string))]
        .sort((a, b) => b.localeCompare(a));
      if (!seasons.includes(currentYear)) seasons.unshift(currentYear);
      setAvailableSeasons(seasons);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCampPhotos = async () => {
    try {
      const { data, error } = await supabase
        .from('kids_camp_photos')
        .select('*')
        .order('year', { ascending: false })
        .order('display_order', { ascending: true });

      if (error) throw error;
      setCampPhotos(data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setContactSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    }
  };

  const handleSavePhoto = async () => {
    try {
      if (editingPhoto) {
        const { error } = await supabase
          .from('kids_camp_photos')
          .update(photoForm)
          .eq('id', editingPhoto.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('kids_camp_photos')
          .insert([photoForm]);

        if (error) throw error;
      }

      await fetchCampPhotos();
      setShowPhotoModal(false);
      setEditingPhoto(null);
      setPhotoForm({
        image_url: '',
        caption: '',
        year: new Date().getFullYear(),
        display_order: 0,
      });
    } catch (error) {
      console.error('Error saving photo:', error);
      alert('Failed to save photo');
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      const { error } = await supabase
        .from('kids_camp_photos')
        .delete()
        .eq('id', photoId);

      if (error) throw error;
      await fetchCampPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    }
  };

  const handleSaveStats = async () => {
    if (!selectedPlayerForStats) return;
    setIsSavingStats(true);
    try {
      const statsData = {
        player_id: selectedPlayerForStats.id,
        ...statsForm,
        season: selectedStatsSeason,
      };
      let result;
      if (editingStats) {
        result = await supabase
          .from('player_statistics')
          .update(statsData)
          .eq('id', editingStats.id);
      } else {
        result = await supabase
          .from('player_statistics')
          .insert([statsData]);
      }
      if (result.error) throw result.error;
      await fetchPlayerStats();
      setIsDirty(false);
    } catch (error: any) {
      console.error('Error saving stats:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSavingStats(false);
    }
  };

  const handleDeleteStats = async (id: string) => {
    if (!confirm('Delete these statistics?')) return;

    try {
      const { error } = await supabase
        .from('player_statistics')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchPlayerStats();
    } catch (error: any) {
      console.error('Error deleting stats:', error);
      alert(`Error: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!selectedPlayerForStats) {
      setIsDirty(false);
      return;
    }
    const existingStat = playerStats.find(
      (s: any) => s.player_id === selectedPlayerForStats.id && s.season === selectedStatsSeason
    );
    if (existingStat) {
      setStatsForm({
        season: existingStat.season,
        games_played: existingStat.games_played || 0,
        at_bats: existingStat.at_bats || 0,
        hits: existingStat.hits || 0,
        runs: existingStat.runs || 0,
        rbis: existingStat.rbis || 0,
        doubles: existingStat.doubles || 0,
        triples: existingStat.triples || 0,
        home_runs: existingStat.home_runs || 0,
        walks: existingStat.walks || 0,
        strikeouts: existingStat.strikeouts || 0,
        stolen_bases: existingStat.stolen_bases || 0,
        innings_pitched: Number(existingStat.innings_pitched) || 0,
        earned_runs: existingStat.earned_runs || 0,
        pitching_strikeouts: existingStat.pitching_strikeouts || 0,
        walks_allowed: existingStat.walks_allowed || 0,
        putouts: existingStat.putouts || 0,
        assists: existingStat.assists || 0,
        errors: existingStat.errors || 0,
      });
      setEditingStats(existingStat);
    } else {
      setStatsForm({
        season: selectedStatsSeason,
        games_played: 0, at_bats: 0, hits: 0, runs: 0, rbis: 0,
        doubles: 0, triples: 0, home_runs: 0, walks: 0, strikeouts: 0,
        stolen_bases: 0, innings_pitched: 0, earned_runs: 0,
        pitching_strikeouts: 0, walks_allowed: 0, putouts: 0, assists: 0, errors: 0,
      });
      setEditingStats(null);
    }
    setIsDirty(false);
  }, [selectedPlayerForStats?.id, selectedStatsSeason, playerStats]);

  const handleGameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...gameForm,
        time2: gameForm.isDoubleHeader ? gameForm.time2 || null : null,
      };
      if (editingGame) {
        await updateGame(editingGame.id, payload);
      } else {
        await addGame(payload);
      }
      setShowGameModal(false);
      resetGameForm();
      await fetchAllData();
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const handleOpenScoreModal = (game: any) => {
    setScoringGame(game);
    if (game.isDoubleHeader) {
      setScoreForm({
        cardinals: '',
        opponent: '',
        game1_cardinals: game.score?.game1?.cardinals?.toString() || '',
        game1_opponent: game.score?.game1?.opponent?.toString() || '',
        game2_cardinals: game.score?.game2?.cardinals?.toString() || '',
        game2_opponent: game.score?.game2?.opponent?.toString() || '',
      });
    } else {
      setScoreForm({
        cardinals: game.score?.cardinals?.toString() || '',
        opponent: game.score?.opponent?.toString() || '',
        game1_cardinals: '',
        game1_opponent: '',
        game2_cardinals: '',
        game2_opponent: '',
      });
    }
    setShowScoreModal(true);
  };

  const handleSaveScore = async () => {
    if (!scoringGame) return;

    try {
      let score: any;
      if (scoringGame.isDoubleHeader) {
        score = {};
        if (scoreForm.game1_cardinals !== '' && scoreForm.game1_opponent !== '') {
          score.game1 = {
            cardinals: parseInt(scoreForm.game1_cardinals),
            opponent: parseInt(scoreForm.game1_opponent),
          };
        }
        if (scoreForm.game2_cardinals !== '' && scoreForm.game2_opponent !== '') {
          score.game2 = {
            cardinals: parseInt(scoreForm.game2_cardinals),
            opponent: parseInt(scoreForm.game2_opponent),
          };
        }
      } else {
        if (scoreForm.cardinals !== '' && scoreForm.opponent !== '') {
          score = {
            cardinals: parseInt(scoreForm.cardinals),
            opponent: parseInt(scoreForm.opponent),
          };
        } else {
          score = null;
        }
      }

      await updateGame(scoringGame.id, { score });
      setShowScoreModal(false);
      setScoringGame(null);
      await fetchAllData();
    } catch (error) {
      console.error('Error saving score:', error);
      alert('Failed to save score');
    }
  };

  const handlePlayerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPlayer) {
        await updatePlayer(editingPlayer.id, playerForm);
      } else {
        await addPlayer(playerForm);
      }
      setShowPlayerModal(false);
      resetPlayerForm();
      await fetchAllData();
    } catch (error) {
      console.error('Error saving player:', error);
    }
  };

  const handleSponsorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSponsor) {
        await updateSponsor(editingSponsor.id, sponsorForm);
      } else {
        await addSponsor(sponsorForm);
      }
      setShowSponsorModal(false);
      resetSponsorForm();
      await fetchAllData();
    } catch (error) {
      console.error('Error saving sponsor:', error);
    }
  };

  const handleEditGame = (game: any) => {
    setEditingGame(game);
    setGameForm({
      date: game.date,
      time: game.time,
      time2: game.time2 || '',
      opponent: game.opponent,
      location: game.location,
      venue: game.venue,
      address: game.address || '',
      isDoubleHeader: game.isDoubleHeader,
    });
    setShowGameModal(true);
  };

  const handleEditPlayer = (player: any) => {
    setEditingPlayer(player);
    setPlayerForm({
      name: player.name,
      number: player.number,
      positions: Array.isArray(player.positions) ? player.positions : [player.positions],
      image: player.image,
      bio: player.bio,
      hometown: player.hometown,
      role: player.role,
      title: player.title || '',
    });
    setShowPlayerModal(true);
  };

  const handleEditSponsor = (sponsor: any) => {
    setEditingSponsor(sponsor);
    setSponsorForm({
      name: sponsor.name,
      logo: sponsor.logo,
      website: sponsor.website,
      description: sponsor.description,
      tier: sponsor.tier,
      active: sponsor.active,
    });
    setShowSponsorModal(true);
  };

  const handlePositionChange = (position: string, checked: boolean) => {
    if (checked) {
      setPlayerForm((prev) => ({
        ...prev,
        positions: [...prev.positions, position],
      }));
    } else {
      setPlayerForm((prev) => ({
        ...prev,
        positions: prev.positions.filter((p) => p !== position),
      }));
    }
  };

  const handleDeleteGame = async (id: string) => {
    if (confirm('Are you sure you want to delete this game?')) {
      await deleteGame(id);
      await fetchAllData();
    }
  };

  const handleDeletePlayer = async (id: string) => {
    if (confirm('Are you sure you want to delete this player?')) {
      await deletePlayer(id);
      await fetchAllData();
    }
  };

  const handleDeleteSponsor = async (id: string) => {
    if (confirm('Are you sure you want to delete this sponsor?')) {
      await deleteSponsor(id);
      await fetchAllData();
    }
  };

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-cardinal mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-[#111111]">
        <Navbar />
        <main className="flex-1 mt-24 flex items-center justify-center">
          <div className="bg-[#222222] p-8 rounded-lg max-w-md w-full">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-cardinal" />
              <h1 className="text-2xl font-bold text-white">Admin Login</h1>
            </div>

            {authError && (
              <div className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4">
                {authError}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  placeholder="admin@example.com"
                  required
                  disabled={loggingIn}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData((prev) => ({ ...prev, password: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50 pr-10"
                    placeholder="Password"
                    required
                    disabled={loggingIn}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    disabled={loggingIn}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loggingIn}
                className="w-full px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loggingIn ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-cardinal mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />

      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Shield className="w-8 h-8 text-cardinal" />
              <h1 className="text-3xl sm:text-4xl font-bold text-white">Admin Dashboard</h1>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {[
              { id: 'games', label: 'Games', icon: Calendar },
              { id: 'players', label: 'Players', icon: Users },
              { id: 'sponsors', label: 'Sponsors', icon: Building2 },
              { id: 'statistics', label: 'Statistics', icon: BarChart3 },
              { id: 'photos', label: 'Camp Photos', icon: ImageIcon },
              { id: 'contact', label: 'Contact Forms', icon: Mail },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => {
                  setActiveTab(id as 'games' | 'players' | 'sponsors' | 'statistics' | 'photos' | 'contact');
                  if (id === 'statistics') fetchPlayerStats();
                  if (id === 'photos') fetchCampPhotos();
                  if (id === 'contact') fetchContactSubmissions();
                }}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
                  activeTab === id
                    ? 'bg-cardinal text-white'
                    : 'bg-[#222222] text-gray-400 hover:bg-[#333333]'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {activeTab === 'games' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Manage Games</h2>
                <button
                  onClick={() => setShowGameModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors w-full sm:w-auto justify-center"
                >
                  <Plus className="w-5 h-5" />
                  Add Game
                </button>
              </div>

              <div className="grid gap-4">
                {games.map((game) => (
                  <div
                    key={game.id}
                    className="bg-[#222222] p-4 sm:p-6 rounded-lg border border-white/10"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                          {game.location === 'Home' ? 'vs' : 'at'} {game.opponent}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-400">
                          {game.date} at {game.time}
                        </p>
                        <p className="text-sm sm:text-base text-gray-400">{game.venue}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {game.isDoubleHeader && (
                            <span className="inline-block px-2 py-1 bg-cardinal text-white text-xs rounded">
                              Double Header
                            </span>
                          )}
                          {game.score && (
                            <span className="inline-block px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded font-medium">
                              {game.isDoubleHeader
                                ? `G1: ${game.score.game1?.cardinals ?? '-'}-${game.score.game1?.opponent ?? '-'} | G2: ${game.score.game2?.cardinals ?? '-'}-${game.score.game2?.opponent ?? '-'}`
                                : `Final: ${game.score.cardinals}-${game.score.opponent}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handleOpenScoreModal(game)}
                          className="flex-1 sm:flex-none p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                          title="Enter Score"
                        >
                          <Trophy className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleEditGame(game)}
                          className="flex-1 sm:flex-none p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleDeleteGame(game.id)}
                          className="flex-1 sm:flex-none p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'players' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Manage Players</h2>
                <button
                  onClick={() => setShowPlayerModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors w-full sm:w-auto justify-center"
                >
                  <Plus className="w-5 h-5" />
                  Add Player
                </button>
              </div>

              <div className="grid gap-4">
                {players.map((player) => (
                  <div
                    key={player.id}
                    className="bg-[#222222] p-4 sm:p-6 rounded-lg border border-white/10"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex gap-3 sm:gap-4 w-full sm:w-auto">
                        <img
                          src={player.image}
                          alt={player.name}
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-bold text-white mb-1 truncate">{player.name}</h3>
                          <p className="text-sm sm:text-base text-gray-400">#{player.number}</p>
                          <p className="text-sm sm:text-base text-gray-400 truncate">
                            {Array.isArray(player.positions)
                              ? player.positions.join(', ')
                              : player.positions}
                          </p>
                          <p className="text-sm sm:text-base text-gray-400 capitalize">{player.role}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handleEditPlayer(player)}
                          className="flex-1 sm:flex-none p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleDeletePlayer(player.id)}
                          className="flex-1 sm:flex-none p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'sponsors' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Manage Sponsors</h2>
                <button
                  onClick={() => setShowSponsorModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors w-full sm:w-auto justify-center"
                >
                  <Plus className="w-5 h-5" />
                  Add Sponsor
                </button>
              </div>

              <div className="grid gap-4">
                {sponsors.map((sponsor) => (
                  <div
                    key={sponsor.id}
                    className="bg-[#222222] p-4 sm:p-6 rounded-lg border border-white/10"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex gap-3 sm:gap-4 w-full sm:w-auto">
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-contain bg-white/10 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-bold text-white mb-1 truncate">{sponsor.name}</h3>
                          <p className="text-sm sm:text-base text-gray-400 capitalize">{sponsor.tier} Tier</p>
                          <p className="text-sm sm:text-base text-gray-400">
                            {sponsor.active ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handleEditSponsor(sponsor)}
                          className="flex-1 sm:flex-none p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleDeleteSponsor(sponsor.id)}
                          className="flex-1 sm:flex-none p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'statistics' && (
            <div className="space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Player Statistics</h2>

              <div className="bg-[#222222] rounded-lg p-6 border border-white/10 space-y-6">
                {/* Player + Season selectors */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Player</label>
                    <select
                      value={selectedPlayerForStats?.id || ''}
                      onChange={(e) => {
                        const player = players.find(p => p.id === e.target.value);
                        setSelectedPlayerForStats(player || null);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    >
                      <option value="">Select a player...</option>
                      {players.filter(p => p.role === 'player').map(player => (
                        <option key={player.id} value={player.id}>{player.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Season</label>
                    {showNewSeasonInput ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="2000"
                          max="2099"
                          value={newSeasonInput}
                          onChange={(e) => setNewSeasonInput(e.target.value)}
                          placeholder="e.g. 2027"
                          className="flex-1 px-3 py-2 rounded-lg bg-[#333333] text-white border border-cardinal focus:border-cardinal focus:ring focus:ring-cardinal/50"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const yr = newSeasonInput.trim();
                            if (yr) {
                              setAvailableSeasons(prev => [...new Set([...prev, yr])].sort((a, b) => b.localeCompare(a)));
                              setSelectedStatsSeason(yr);
                            }
                            setShowNewSeasonInput(false);
                            setNewSeasonInput('');
                          }}
                          className="px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowNewSeasonInput(false); setNewSeasonInput(''); }}
                          className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select
                          value={selectedStatsSeason}
                          onChange={(e) => setSelectedStatsSeason(e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                        >
                          {availableSeasons.map(s => (
                            <option key={s} value={s}>{s} Season</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowNewSeasonInput(true)}
                          className="px-3 py-2 bg-[#333333] hover:bg-[#444444] text-gray-300 hover:text-white border border-gray-700 rounded-lg transition-colors text-sm whitespace-nowrap"
                        >
                          + New Year
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {selectedPlayerForStats ? (
                  <div className="space-y-6">
                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-white font-semibold mb-3">Batting</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { label: 'Games Played', key: 'games_played' },
                          { label: 'At Bats', key: 'at_bats' },
                          { label: 'Hits', key: 'hits' },
                          { label: 'Runs', key: 'runs' },
                          { label: 'RBIs', key: 'rbis' },
                          { label: 'Doubles', key: 'doubles' },
                          { label: 'Triples', key: 'triples' },
                          { label: 'Home Runs', key: 'home_runs' },
                          { label: 'Walks', key: 'walks' },
                          { label: 'Strikeouts', key: 'strikeouts' },
                          { label: 'Stolen Bases', key: 'stolen_bases' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
                            <input
                              type="number"
                              min="0"
                              value={(statsForm as any)[key]}
                              onChange={(e) => {
                                setStatsForm({ ...statsForm, [key]: parseInt(e.target.value) || 0 });
                                setIsDirty(true);
                              }}
                              className="w-full px-3 py-2 rounded-lg bg-[#1A1A1A] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-white font-semibold mb-3">Pitching</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">Innings Pitched</label>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={statsForm.innings_pitched}
                            onChange={(e) => { setStatsForm({ ...statsForm, innings_pitched: parseFloat(e.target.value) || 0 }); setIsDirty(true); }}
                            className="w-full px-3 py-2 rounded-lg bg-[#1A1A1A] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50 text-sm"
                          />
                        </div>
                        {[
                          { label: 'Earned Runs', key: 'earned_runs' },
                          { label: 'Strikeouts (P)', key: 'pitching_strikeouts' },
                          { label: 'Walks Allowed', key: 'walks_allowed' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
                            <input
                              type="number"
                              min="0"
                              value={(statsForm as any)[key]}
                              onChange={(e) => {
                                setStatsForm({ ...statsForm, [key]: parseInt(e.target.value) || 0 });
                                setIsDirty(true);
                              }}
                              className="w-full px-3 py-2 rounded-lg bg-[#1A1A1A] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-white font-semibold mb-3">Fielding</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { label: 'Putouts', key: 'putouts' },
                          { label: 'Assists', key: 'assists' },
                          { label: 'Errors', key: 'errors' },
                        ].map(({ label, key }) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
                            <input
                              type="number"
                              min="0"
                              value={(statsForm as any)[key]}
                              onChange={(e) => {
                                setStatsForm({ ...statsForm, [key]: parseInt(e.target.value) || 0 });
                                setIsDirty(true);
                              }}
                              className="w-full px-3 py-2 rounded-lg bg-[#1A1A1A] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-5 flex items-center justify-between">
                      <p className="text-sm text-gray-500">
                        {editingStats ? `Editing ${selectedStatsSeason} season` : `New entry for ${selectedStatsSeason} season`}
                      </p>
                      <button
                        onClick={handleSaveStats}
                        disabled={isSavingStats || !isDirty}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all duration-200
                          ${isDirty
                            ? 'bg-cardinal hover:bg-cardinal-hover text-white shadow-lg shadow-cardinal/20'
                            : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
                      >
                        <Save className="w-4 h-4" />
                        {isSavingStats ? 'Saving...' : isDirty ? 'Save Changes' : 'Saved'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 border-t border-gray-700">
                    <BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">Select a player above to view and edit their statistics</p>
                  </div>
                )}
              </div>

              {playerStats.length > 0 && (
                <div className="bg-[#222222] rounded-lg border border-white/10 overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-semibold text-white">All Saved Season Records</h3>
                  </div>
                  <div className="divide-y divide-white/5">
                    {playerStats.map((stat: any) => {
                      const player = players.find(p => p.id === stat.player_id);
                      const isActive = selectedPlayerForStats?.id === stat.player_id && selectedStatsSeason === stat.season;
                      return (
                        <div
                          key={stat.id}
                          className={`p-4 flex items-center justify-between transition-colors ${isActive ? 'bg-cardinal/10' : 'hover:bg-white/5'}`}
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="min-w-0">
                              <p className="text-white font-medium truncate">{player?.name || 'Unknown'}</p>
                              <p className="text-cardinal text-sm font-medium">{stat.season} Season</p>
                            </div>
                            <div className="hidden sm:flex gap-4 text-sm text-gray-400">
                              <span>G: {stat.games_played}</span>
                              <span>AVG: {stat.batting_average?.toFixed(3) || '.000'}</span>
                              <span>H: {stat.hits}</span>
                              <span>RBI: {stat.rbis}</span>
                              <span>HR: {stat.home_runs}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setSelectedPlayerForStats(player);
                                setSelectedStatsSeason(stat.season);
                              }}
                              className={`p-2 rounded-lg transition-colors ${isActive ? 'bg-cardinal text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                              title="Load this season for editing"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStats(stat.id)}
                              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}


          {activeTab === 'photos' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Kids Camp Photos</h2>
                <button
                  onClick={() => {
                    setShowPhotoModal(true);
                    setEditingPhoto(null);
                    setPhotoForm({
                      image_url: '',
                      caption: '',
                      year: new Date().getFullYear(),
                      display_order: 0,
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors w-full sm:w-auto justify-center"
                >
                  <Plus className="w-5 h-5" />
                  Add Photo
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {campPhotos.map((photo) => (
                  <div
                    key={photo.id}
                    className="bg-[#222222] rounded-lg overflow-hidden border border-white/10"
                  >
                    <div className="aspect-square bg-[#1A1A1A]">
                      <img
                        src={photo.image_url}
                        alt={photo.caption || 'Camp photo'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-white text-sm font-medium">{photo.year}</p>
                      {photo.caption && (
                        <p className="text-gray-400 text-xs mt-1 line-clamp-2">{photo.caption}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">Order: {photo.display_order}</p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => {
                            setEditingPhoto(photo);
                            setPhotoForm(photo);
                            setShowPhotoModal(true);
                          }}
                          className="flex-1 p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="flex-1 p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {campPhotos.length === 0 && (
                <div className="text-center py-12 bg-[#222222] rounded-lg">
                  <ImageIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No photos yet. Add your first camp photo!</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Contact Form Submissions</h2>
              </div>

              <div className="space-y-4">
                {contactSubmissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="bg-[#222222] rounded-lg p-4 sm:p-6 border border-white/10"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <Mail className="w-5 h-5 text-cardinal mt-1" />
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-white">{submission.subject}</h3>
                            <p className="text-sm text-gray-400 mt-1">
                              From: {submission.name} ({submission.email})
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-400">
                        {new Date(submission.submitted_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <div className="bg-[#1A1A1A] rounded-lg p-4 border border-white/5">
                      <p className="text-gray-300 whitespace-pre-wrap">{submission.message}</p>
                    </div>
                  </div>
                ))}
              </div>

              {contactSubmissions.length === 0 && (
                <div className="text-center py-12 bg-[#222222] rounded-lg">
                  <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No contact form submissions yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingPhoto ? 'Edit Photo' : 'Add Photo'}
              </h3>
              <button
                onClick={() => {
                  setShowPhotoModal(false);
                  setEditingPhoto(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Image URL *
                </label>
                <input
                  type="url"
                  value={photoForm.image_url}
                  onChange={(e) => setPhotoForm({ ...photoForm, image_url: e.target.value })}
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-white/10 rounded-lg text-white"
                  placeholder="https://example.com/photo.jpg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={photoForm.caption}
                  onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })}
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-white/10 rounded-lg text-white"
                  placeholder="Photo description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  value={photoForm.year}
                  onChange={(e) => setPhotoForm({ ...photoForm, year: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-white/10 rounded-lg text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={photoForm.display_order}
                  onChange={(e) => setPhotoForm({ ...photoForm, display_order: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-white/10 rounded-lg text-white"
                />
              </div>

              {photoForm.image_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Preview</label>
                  <div className="aspect-square bg-[#1A1A1A] rounded-lg overflow-hidden">
                    <img
                      src={photoForm.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '';
                        e.currentTarget.alt = 'Invalid image URL';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPhotoModal(false);
                  setEditingPhoto(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePhoto}
                disabled={!photoForm.image_url}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showGameModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingGame ? 'Edit Game' : 'Add New Game'}
              </h3>
              <button
                onClick={() => {
                  setShowGameModal(false);
                  resetGameForm();
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleGameSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={gameForm.date}
                    onChange={(e) => setGameForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Time</label>
                  <input
                    type="time"
                    value={gameForm.time}
                    onChange={(e) => setGameForm((prev) => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Opponent</label>
                <input
                  type="text"
                  value={gameForm.opponent}
                  onChange={(e) =>
                    setGameForm((prev) => ({ ...prev, opponent: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Location</label>
                <select
                  value={gameForm.location}
                  onChange={(e) =>
                    setGameForm((prev) => ({
                      ...prev,
                      location: e.target.value as 'Home' | 'Away',
                    }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                >
                  <option value="Home">Home</option>
                  <option value="Away">Away</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Venue</label>
                <input
                  type="text"
                  value={gameForm.venue}
                  onChange={(e) => setGameForm((prev) => ({ ...prev, venue: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Address (Optional)
                </label>
                <input
                  type="text"
                  value={gameForm.address}
                  onChange={(e) => setGameForm((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="doubleHeader"
                  checked={gameForm.isDoubleHeader}
                  onChange={(e) =>
                    setGameForm((prev) => ({ ...prev, isDoubleHeader: e.target.checked }))
                  }
                  className="rounded border-gray-700 bg-[#333333] text-cardinal focus:ring-cardinal"
                />
                <label htmlFor="doubleHeader" className="text-sm text-gray-400">
                  Double Header
                </label>
              </div>

              {gameForm.isDoubleHeader && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Game 2 Time
                  </label>
                  <input
                    type="time"
                    value={gameForm.time2}
                    onChange={(e) => setGameForm((prev) => ({ ...prev, time2: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowGameModal(false);
                    resetGameForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingGame ? 'Update Game' : 'Add Game'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPlayerModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingPlayer ? 'Edit Player' : 'Add New Player'}
              </h3>
              <button
                onClick={() => {
                  setShowPlayerModal(false);
                  resetPlayerForm();
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handlePlayerSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={playerForm.name}
                  onChange={(e) => setPlayerForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Number</label>
                  <input
                    type="text"
                    value={playerForm.number}
                    onChange={(e) =>
                      setPlayerForm((prev) => ({ ...prev, number: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Role</label>
                  <select
                    value={playerForm.role}
                    onChange={(e) =>
                      setPlayerForm((prev) => ({
                        ...prev,
                        role: e.target.value as 'player' | 'gameday',
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  >
                    <option value="player">Player</option>
                    <option value="gameday">Gameday Staff</option>
                  </select>
                </div>
              </div>

              {playerForm.role === 'player' && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Positions</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['LHP', 'RHP', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'OF', 'IF', 'UTL'].map(
                      (position) => (
                        <label key={position} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={playerForm.positions.includes(position)}
                            onChange={(e) =>
                              handlePositionChange(position, e.target.checked)
                            }
                            className="rounded border-gray-700 bg-[#333333] text-cardinal focus:ring-cardinal"
                          />
                          <span className="text-sm text-gray-400">{position}</span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              )}

              {playerForm.role === 'gameday' && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Title</label>
                  <input
                    type="text"
                    value={playerForm.title}
                    onChange={(e) => setPlayerForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    placeholder="e.g., Scorekeeper, Equipment Manager"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Image URL</label>
                <input
                  type="url"
                  value={playerForm.image}
                  onChange={(e) => setPlayerForm((prev) => ({ ...prev, image: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Bio</label>
                <textarea
                  value={playerForm.bio}
                  onChange={(e) => setPlayerForm((prev) => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Hometown</label>
                <input
                  type="text"
                  value={playerForm.hometown}
                  onChange={(e) =>
                    setPlayerForm((prev) => ({ ...prev, hometown: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPlayerModal(false);
                    resetPlayerForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingPlayer ? 'Update Player' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSponsorModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                {editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}
              </h3>
              <button
                onClick={() => {
                  setShowSponsorModal(false);
                  resetSponsorForm();
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSponsorSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={sponsorForm.name}
                  onChange={(e) => setSponsorForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={sponsorForm.logo}
                  onChange={(e) => setSponsorForm((prev) => ({ ...prev, logo: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Website</label>
                <input
                  type="url"
                  value={sponsorForm.website}
                  onChange={(e) =>
                    setSponsorForm((prev) => ({ ...prev, website: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  value={sponsorForm.description}
                  onChange={(e) =>
                    setSponsorForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Tier</label>
                <select
                  value={sponsorForm.tier}
                  onChange={(e) =>
                    setSponsorForm((prev) => ({
                      ...prev,
                      tier: e.target.value as 'gold' | 'silver',
                    }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                >
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={sponsorForm.active}
                  onChange={(e) =>
                    setSponsorForm((prev) => ({ ...prev, active: e.target.checked }))
                  }
                  className="rounded border-gray-700 bg-[#333333] text-cardinal focus:ring-cardinal"
                />
                <label htmlFor="active" className="text-sm text-gray-400">
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSponsorModal(false);
                    resetSponsorForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingSponsor ? 'Update Sponsor' : 'Add Sponsor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

            {showScoreModal && scoringGame && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">
                Enter Score
              </h3>
              <button
                onClick={() => {
                  setShowScoreModal(false);
                  setScoringGame(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-gray-400 mb-4">
              {scoringGame.location === 'Home' ? 'vs' : 'at'} {scoringGame.opponent} — {scoringGame.date}
            </p>

            {scoringGame.isDoubleHeader ? (
              <div className="space-y-6">
                <div>
                  <h4 className="text-white font-semibold mb-3">Game 1</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Cardinals</label>
                      <input
                        type="number"
                        min="0"
                        value={scoreForm.game1_cardinals}
                        onChange={(e) => setScoreForm({ ...scoreForm, game1_cardinals: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">{scoringGame.opponent}</label>
                      <input
                        type="number"
                        min="0"
                        value={scoreForm.game1_opponent}
                        onChange={(e) => setScoreForm({ ...scoreForm, game1_opponent: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-3">Game 2</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Cardinals</label>
                      <input
                        type="number"
                        min="0"
                        value={scoreForm.game2_cardinals}
                        onChange={(e) => setScoreForm({ ...scoreForm, game2_cardinals: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">{scoringGame.opponent}</label>
                      <input
                        type="number"
                        min="0"
                        value={scoreForm.game2_opponent}
                        onChange={(e) => setScoreForm({ ...scoreForm, game2_opponent: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Cardinals</label>
                  <input
                    type="number"
                    min="0"
                    value={scoreForm.cardinals}
                    onChange={(e) => setScoreForm({ ...scoreForm, cardinals: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">{scoringGame.opponent}</label>
                  <input
                    type="number"
                    min="0"
                    value={scoreForm.opponent}
                    onChange={(e) => setScoreForm({ ...scoreForm, opponent: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-[#333333] text-white border border-gray-700 focus:border-cardinal focus:ring focus:ring-cardinal/50"
                    placeholder="0"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowScoreModal(false);
                  setScoringGame(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScore}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg transition-colors"
              >
                <Save className="w-5 h-5" />
                Save Score
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Admin;
